import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { expireLocalSessionIfNeeded, hasCurrentSession, refreshSession, subscribeToSession } from "../api/client";

// SessionGate keeps the "is there a session?" question in exactly one place.
// It never renders children until /admin/v1/session/me has answered.
export function SessionGate({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [state, setState] = useState<"checking" | "authenticated" | "anonymous" | "unavailable">(
    hasCurrentSession() ? "authenticated" : "checking",
  );

  useEffect(() => {
    expireLocalSessionIfNeeded();
    return subscribeToSession((authenticated) => {
      if (!authenticated) {
        setState("anonymous");
        navigate("/login", { replace: true });
      }
    });
  }, [navigate]);

  async function checkSession() {
    setState("checking");
    const result = await refreshSession();
    if (result === "authenticated") setState("authenticated");
    if (result === "unauthenticated") {
      setState("anonymous");
      navigate("/login", { replace: true });
    }
    if (result === "unavailable") setState("unavailable");
  }

  useEffect(() => {
    if (hasCurrentSession()) return;
    let cancelled = false;
    void refreshSession().then((result) => {
      if (cancelled) return;
      if (result === "authenticated") setState("authenticated");
      if (result === "unauthenticated") {
        setState("anonymous");
        navigate("/login", { replace: true });
      }
      if (result === "unavailable") setState("unavailable");
    });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (state === "checking") return <p style={{ padding: 24 }}>Проверяю сессию…</p>;
  if (state === "unavailable") {
    return (
      <div style={{ padding: 24 }}>
        <p className="error" role="alert">Не удалось проверить сессию.</p>
        <button type="button" onClick={() => void checkSession()}>Повторить</button>
      </div>
    );
  }
  if (state === "anonymous") return null;
  return <>{children}</>;
}
